import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import type { ThemeMode } from '../../../src/theme/ThemeContext';

export const getMemberProfileViewStyles = (theme: Theme, themeMode: ThemeMode, windowWidth = 390) => {
  const isDark = themeMode === 'dark';
  const wcWebMaxWidth = Platform.OS === 'web'
    ? (windowWidth >= 1024 ? 880 : windowWidth >= 768 ? 700 : 560)
    : undefined;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    scrollContent: {
      paddingBottom: 100,
      paddingHorizontal: 16,
      ...(wcWebMaxWidth != null && {
        maxWidth: wcWebMaxWidth,
        width: '100%' as never,
        alignSelf: 'center' as const,
      }),
    },
    backButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      paddingHorizontal: 16,
      paddingVertical: 10,
      alignSelf: 'flex-start',
    },
    backButtonPressed: { opacity: 0.7 },
    backButtonHover: { opacity: 0.85 },
    backLabel: {
      fontSize: 14,
      fontWeight: '600',
      color: theme.primary,
    },
    loadingState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
    },
    errorState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
    },
    errorText: {
      fontSize: 14,
      color: theme.gray[500],
      textAlign: 'center',
    },
    profileHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 12,
      paddingBottom: 16,
      gap: 12,
    },
    memberName: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.gray[900],
      flexShrink: 1,
    },
    statsRow: {
      flexDirection: 'row',
      gap: 10,
      marginBottom: 20,
    },
    statChip: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: isDark ? theme.background.cardElevated : theme.background.card,
      borderRadius: 12,
      paddingVertical: 10,
      paddingHorizontal: 10,
      borderWidth: 1,
      borderColor: isDark ? theme.primary + '33' : theme.gray[200],
    },
    statValue: {
      fontSize: 15,
      fontWeight: '700',
      color: theme.gray[900],
    },
    /** Settled/live split under the points chip. Muted: the headline stays the total. */
    pointsBreakdown: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[400],
      textAlign: 'center',
      marginTop: 4,
    },
    statLabel: {
      fontSize: 11,
      fontWeight: '500',
      color: theme.gray[500],
    },
    selfReadOnlyHint: {
      fontSize: 12,
      color: theme.gray[500],
      textAlign: 'center',
      lineHeight: 17,
      marginTop: 2,
      marginBottom: 14,
      paddingHorizontal: 12,
    },
    sectionTitle: {
      fontSize: 12,
      fontWeight: '800',
      letterSpacing: 0.8,
      color: theme.gray[600],
      marginBottom: 10,
      textTransform: 'uppercase',
    },
    futuresRow: {
      flexDirection: 'row',
      gap: 10,
      marginBottom: 20,
    },
    futureChip: {
      flex: 1,
      backgroundColor: isDark ? theme.background.cardElevated : theme.background.card,
      borderRadius: 14,
      padding: 12,
      borderWidth: 1,
      borderColor: isDark ? theme.primary + '33' : theme.gray[200],
      alignItems: 'center',
      gap: 6,
    },
    futureChipLabel: {
      fontSize: 11,
      fontWeight: '700',
      letterSpacing: 0.5,
      color: theme.gray[500],
      textTransform: 'uppercase',
    },
    futureChipMedia: {
      width: 44,
      height: 34,
      borderRadius: 4,
      overflow: 'visible',
    },
    futureFlag: {
      width: 44,
      height: 30,
      borderRadius: 4,
    },
    futureFlagEmpty: {
      width: 44,
      height: 30,
      borderRadius: 4,
      backgroundColor: isDark ? theme.gray[700] : theme.gray[100],
      alignItems: 'center',
      justifyContent: 'center',
    },
    scorerKitOverlay: {
      position: 'absolute',
      bottom: -4,
      right: -6,
    },
    futureChipNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    futureChipName: {
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[800],
      flexShrink: 1,
    },
    selfNameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexShrink: 1,
    },
    rankBadge: {
      backgroundColor: isDark ? theme.primary + '2A' : theme.primary + '14',
      borderRadius: 8,
      paddingHorizontal: 8,
      paddingVertical: 3,
    },
    rankBadgeText: {
      fontSize: 13,
      fontWeight: '800',
      color: theme.primary,
    },
    matchCardWrapper: {
      marginBottom: 10,
    },
    emptyMatches: {
      alignItems: 'center',
      paddingTop: 32,
    },
    emptyMatchesText: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
    },
  });
};
